import {uniq} from 'ramda';

import {useListFeatureQuery, type FeatureResponseBody} from '@dms/api/new-vehicle';

import {t} from '../utils/catalogueUi';

/**
 * The model year's items, plus the outside items among `ids` that a saved rule or condition still
 * names, marked as not used by the year. Only such an outside id loads the tenant library, and every
 * caller shares that one cached list. Until every outside id has a name the result is loading or
 * failed: a choice field drops a value it has no option for, so a field holding one must not be
 * edited meanwhile.
 */
export function useYearFeatureNames(yearFeatures: FeatureResponseBody[], ids: string[]) {
  const outsideIds = uniq(ids).filter((id) => !yearFeatures.some((feature) => feature.id === id));
  const library = useListFeatureQuery({}, {skip: outsideIds.length === 0});
  const outsideFeatures = (library.data?.items ?? [])
    .filter(({id}) => outsideIds.includes(id))
    .map((feature) => ({
      ...feature,
      name: t(
        'entity.newVehicle.catalogue.notInModelYear',
        '{name} (not used by this model year)',
        {
          name: feature.name,
        }
      ),
    }));
  const isUnnamed = outsideFeatures.length < outsideIds.length;
  const isLoading = isUnnamed && !library.data && !library.isError;
  return {
    features: [...yearFeatures, ...outsideFeatures],
    outsideIds,
    isLoading,
    isError: isUnnamed && !isLoading,
  };
}
